import { useEffect, useRef, useState } from 'react';

import {
  emitMintedReferenceLog,
  resolveErrorReference,
} from './resolve-error-reference.js';

export type ErrorReferenceInput = {
  /**
   * The backend's request id, when the error came from an `ApiError` envelope.
   * When present it becomes the reference and **no** client log is emitted —
   * the backend already recorded the error under this same id (Cloud Logging,
   * plus the Sentry `request_id` tag for unhandled 5xx). When absent, a UUID is
   * minted and a single `console.error` is emitted, forwarded to Sentry as a
   * log — the only case the SPA owns the record.
   */
  requestId?: string | undefined;
  /** Stable, non-localised error code — shown in the UI and logged. */
  code: string;
  /** The console.error line, '[Label] …' convention. Used only when minting. */
  logMessage: string;
  /** Extra structured fields merged into the minted log. No secrets / PII. */
  logContext?: Record<string, string | number | boolean | null>;
};

export type ErrorReference = {
  /** Reference shown by `<DebugBlock>`: the backend `requestId`, or — when the
   *  backend never saw the error — a client UUID matching the Sentry log. */
  reference: string;
  /** When the error surfaced. */
  timestamp: Date;
};

/**
 * Resolves the triage reference an error surface shows, picking the source by
 * where the error was actually recorded:
 *
 * - **backend `ApiError`** (`requestId` set) → reuse that id; emit no log.
 * - **anything else** (a non-`ApiError` failure the backend never saw) → mint a
 *   `crypto.randomUUID()` and emit one `console.error` (→ Sentry log).
 *
 * Render crashes are handled separately — the error boundary supplies Sentry's
 * native `eventId`, so they don't go through this hook.
 *
 * Call this only for *unexpected* failures: when minting, invoking the hook is
 * what emits the log, so an expected outcome must not call it.
 */
export function useErrorReference({
  requestId,
  code,
  logMessage,
  logContext,
}: ErrorReferenceInput): ErrorReference {
  const [resolved] = useState(() => ({
    ...resolveErrorReference(requestId),
    timestamp: new Date(),
  }));

  // `logContext` is typically a fresh object each render, so the effect re-runs;
  // the ref guard keeps the log to exactly one emission per mount.
  const loggedRef = useRef(false);
  useEffect(() => {
    if (!resolved.minted) return; // backend already logged this error
    if (loggedRef.current) return;
    loggedRef.current = true;
    emitMintedReferenceLog(logMessage, {
      reference: resolved.reference,
      code,
      ...logContext,
    });
  }, [code, logMessage, logContext, resolved]);

  return { reference: resolved.reference, timestamp: resolved.timestamp };
}
